/**
 * Hold to confirm: the action fires after a fixed hold (the same duration for every action and
 * every player, §15.1). Releasing early cancels. `whileHolding` replaces the label during the
 * hold, e.g. to show the chosen card only while the finger is down.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export const HOLD_MS = 900;

export interface HoldButtonProps {
  readonly onConfirm: () => void;
  readonly children: ReactNode;
  readonly whileHolding?: ReactNode;
  readonly durationMs?: number;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function HoldButton({
  onConfirm,
  children,
  whileHolding,
  durationMs = HOLD_MS,
  disabled = false,
  className = "btn primary big block",
  testId,
}: HoldButtonProps) {
  const [holding, setHolding] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirm = useRef(onConfirm);
  useEffect(() => {
    confirm.current = onConfirm;
  }, [onConfirm]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const start = () => {
    if (disabled || timer.current) return;
    setHolding(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setHolding(false);
      confirm.current();
    }, durationMs);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setHolding(false);
  };

  return (
    <button
      type="button"
      data-testid={testId}
      className={`hold ${holding ? "holding" : ""} ${className}`}
      style={{ "--hold-ms": `${durationMs}ms` } as CSSProperties}
      disabled={disabled}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        start();
      }}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onLostPointerCapture={cancel}
      onKeyDown={(e) => {
        if ((e.key === " " || e.key === "Enter") && !e.repeat) {
          e.preventDefault();
          start();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Enter") cancel();
      }}
      onBlur={cancel}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="fill" />
      <span className="label">{holding && whileHolding ? whileHolding : children}</span>
    </button>
  );
}
